import { Phone, Shield, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { serviceSchema, breadcrumbSchema, seoKeywords } from '../utils/seoData';

const SnakeRescue = () => {
  const rescueProcess = [
    {
      step: '1',
      title: 'Call Us Immediately',
      description: 'Contact Alhan (+91 8310755854) or Mohan (+91 9845080903). Keep a safe distance from the snake.',
    },
    {
      step: '2',
      title: 'We Arrive Quickly',
      description: 'Our expert arrives at your location with proper equipment.',
    },
    {
      step: '3',
      title: 'Safe Removal',
      description: 'Snake is safely captured using humane methods without harm.',
    },
    {
      step: '4',
      title: 'Release & Advice',
      description: 'Snake released in natural habitat. You get safety tips to prevent future incidents.',
    },
  ];

  const snakeTypes = [
    'Indian Cobra',
    'Russell\'s Viper',
    'Common Krait',
    'Rat Snake',
    'Indian Rock Python',
    'Checkered Keelback',
    'Bronzeback Tree Snake',
    'Garden Lizards',
  ];

  const safetyTips = [
    'Do not attempt to catch or kill the snake',
    'Keep children and pets away from the area',
    'Do not corner or provoke the snake',
    'Close doors to contain it in one room if possible',
    'Turn on lights to monitor from a safe distance',
    'Do not use water or chemicals on the snake',
  ];

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Snake Rescue Services', path: '/snake-rescue' }
  ]);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Snake Rescue Bangalore | Snake Removal Near Me - 24/7 Emergency"
        description="Snake rescue near me in Bangalore - 24/7 emergency snake removal from homes, bathrooms & kitchens. Call Alhan +91 8310755854 or Mohan +91 9845080903."
        keywords={seoKeywords.snakeRescue}
        canonical="/snake-rescue"
        schemaData={[serviceSchema, breadcrumbs]}
      />
      
      {/* Hero */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <AlertTriangle className="inline h-4 w-4 mr-1" />
              Emergency Service
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-emerald-600">Snake Rescue Bangalore</span> — Urgent Snake Removal Near You
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Fast, safe, and humane snake removal by certified experts Alhan Nawaz and Mohan K. Available 24/7 across Bangalore.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="tel:+918310755854">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6">
                  <Phone className="mr-2 h-5 w-5" />
                  Alhan: +91 8310755854
                </Button>
              </a>
              <a href="tel:+919845080903">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6">
                  <Phone className="mr-2 h-5 w-5" />
                  Mohan: +91 9845080903
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Rescue Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
              <p className="text-gray-600">Our simple and effective snake rescue process</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {rescueProcess.map((item, index) => (
                <Card key={index} className="text-center border-emerald-100">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-emerald-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Do */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  <AlertTriangle className="inline h-8 w-8 text-red-600 mr-2" />
                  If You See a Snake
                </h2>
                <p className="text-gray-600 mb-6">
                  Your safety is the top priority. Follow these important guidelines while waiting for our arrival:
                </p>
                <ul className="space-y-3">
                  {safetyTips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{tip}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
                  <p className="text-red-800 font-semibold">
                    ⚠️ Important: Never attempt to handle a snake yourself. Even non-venomous snakes can bite when threatened.
                  </p>
                </div>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  <Shield className="inline h-8 w-8 text-emerald-600 mr-2" />
                  Snakes We Handle
                </h2>
                <p className="text-gray-600 mb-6">
                  We safely rescue all types of snakes commonly found in Bangalore, including:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {snakeTypes.map((snake, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                      <p className="text-gray-800 font-medium">{snake}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-6">
                  <p className="text-emerald-800 font-semibold mb-2">
                    🐍 All snakes are protected under Wildlife Protection Act
                  </p>
                  <p className="text-emerald-700 text-sm">
                    We ensure humane handling and release in appropriate natural habitats.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rescue Gallery */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Real Rescue Operations</h2>
              <p className="text-gray-600">See our expert team in action - safely handling and rescuing snakes</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src="https://customer-assets.emergentagent.com/job_alhan-snakes/artifacts/0cc6b0o2_snake.png" 
                  alt="Professional cobra rescue service in Bangalore - venomous snake removal"
                  className="w-full h-96 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Cobra Rescue Operation</h3>
                  <p className="text-gray-600 mb-3">Expert handling of venomous cobra with proper safety equipment and techniques</p>
                  <div className="flex items-center gap-2 text-sm text-emerald-600 font-semibold">
                    <Shield className="h-4 w-4" />
                    <span>Venomous - Expert Handling Required</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src="https://customer-assets.emergentagent.com/job_alhan-snakes/artifacts/orrlq0z4_snake2.png" 
                  alt="Rat snake rescue service in Bangalore - non-venomous snake removal"
                  className="w-full h-96 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Rat Snake Rescue Operation</h3>
                  <p className="text-gray-600 mb-3">Safe capture and relocation of non-venomous rat snake from residential area</p>
                  <div className="flex items-center gap-2 text-sm text-gray-600 font-semibold">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Non-Venomous - Safe Release in Nature</span>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="text-center mt-8">
              <p className="text-gray-600 mb-4">Every rescue is handled with care and professionalism</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+918310755854">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Phone className="mr-2 h-4 w-4" />
                    Call Alhan: +91 8310755854
                  </Button>
                </a>
                <a href="tel:+919845080903">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Phone className="mr-2 h-4 w-4" />
                    Call Mohan: +91 9845080903
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Professional Help */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Why Choose Professional Snake Rescue?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-emerald-100">
                <CardContent className="p-6 text-center">
                  <Shield className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Your Safety</h3>
                  <p className="text-sm text-gray-600">Avoid bites and injuries from improper handling</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-100">
                <CardContent className="p-6 text-center">
                  <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Proper Equipment</h3>
                  <p className="text-sm text-gray-600">Professional tools for safe snake capture</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-100">
                <CardContent className="p-6 text-center">
                  <Clock className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Quick Response</h3>
                  <p className="text-sm text-gray-600">Fast arrival to resolve the situation quickly</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Snake Rescue? We're Here to Help!
          </h2>
          <p className="text-xl mb-8 text-emerald-50">
            Available 24/7 across Bangalore
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:+918310755854">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Alhan: +91 8310755854
              </Button>
            </a>
            <a href="tel:+919845080903">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Mohan: +91 9845080903
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SnakeRescue;